import { useState, useEffect } from 'react';
import { Star, Shield, Building2, Check, Trash2, AlertCircle, Filter, Download, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Display
const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${
          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'
        }`}
      />
    ))}
  </div>
);

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    gold: 'bg-[#c5a059]/10 text-[#c5a059]'
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Review Row Component
const ReviewRow = ({ review, onApprove, onDelete }) => {
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    await onApprove(review.id);
    setLoading(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this review? The coupon will NOT be revoked.')) {
      setLoading(true);
      await onDelete(review.id);
      setLoading(false);
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-[#1a1a1a]">{review.reviewer_name}</p>
          <p className="text-xs text-gray-500">{review.reviewer_email}</p>
          {review.company_name && (
            <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
              <Building2 className="w-3 h-3" />
              {review.company_name}
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 font-medium">{review.product_name}</p>
      </td>
      <td className="px-4 py-3">
        <StarRating rating={review.rating} />
      </td>
      <td className="px-4 py-3 max-w-xs">
        <p className="text-sm text-gray-600 line-clamp-2" title={review.review_text}>
          {review.review_text}
        </p>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          {review.is_verified && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full w-fit">
              <Shield className="w-3 h-3" />
              Verified
            </span>
          )}
          {review.is_approved ? (
            <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full w-fit">
              <Check className="w-3 h-3" />
              Approved
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full w-fit">
              <AlertCircle className="w-3 h-3" />
              Pending
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {review.coupon_code ? (
          <span className="font-mono text-sm text-[#c5a059]">{review.coupon_code}</span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {new Date(review.created_at).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          {!review.is_approved && (
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Check className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            disabled={loading}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Main Admin Reviews Dashboard
export default function AdminReviewsDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    if (!user || !user.is_admin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, filter, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/reviews?status=${filter}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/reviews/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const reviewsData = await reviewsRes.json();
      const statsData = await statsRes.json();
      
      setReviews(reviewsData.reviews || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Failed to approve review:', err);
    }
  };

  const handleDelete = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };

  const handleSendReviewRequests = async () => {
    setSendingEmails(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/send-requests`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      alert(`Sent ${data.emails_sent} review request emails out of ${data.orders_processed} eligible orders.`);
    } catch (err) {
      console.error('Failed to send review requests:', err);
      alert('Failed to send review request emails.');
    } finally {
      setSendingEmails(false);
    }
  };

  const exportReviews = () => {
    const csv = [
      ['Name', 'Email', 'Company', 'Product', 'Rating', 'Title', 'Review', 'Verified', 'Approved', 'Coupon', 'Date'].join(','),
      ...reviews.map(r => [
        `"${r.reviewer_name}"`,
        r.reviewer_email,
        `"${r.company_name || ''}"`,
        `"${r.product_name}"`,
        r.rating,
        `"${r.title || ''}"`,
        `"${r.review_text.replace(/"/g, '""')}"`,
        r.is_verified ? 'Yes' : 'No',
        r.is_approved ? 'Yes' : 'No',
        r.coupon_code || '',
        new Date(r.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviews_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a1a]">Reviews Management</h1>
            <p className="text-gray-500">Moderate reviews and track coupon redemptions</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSendReviewRequests}
              disabled={sendingEmails}
              className="border-[#c5a059] text-[#c5a059]"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingEmails ? 'Sending...' : 'Send Review Requests'}
            </Button>
            <Button variant="outline" onClick={exportReviews}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <StatsCard title="Total Reviews" value={stats.total_reviews} icon={Star} color="blue" />
            <StatsCard title="Pending" value={stats.pending_reviews} icon={AlertCircle} color="yellow" />
            <StatsCard title="Approved" value={stats.approved_reviews} icon={Check} color="green" />
            <StatsCard title="Verified" value={stats.verified_reviews} icon={Shield} color="purple" />
            <StatsCard title="Coupons Issued" value={stats.coupons_issued} icon={Mail} color="gold" />
            <StatsCard title="Coupons Used" value={stats.coupons_redeemed} icon={Check} color="green" />
            <StatsCard 
              title="Redemption Rate" 
              value={`${stats.redemption_rate}%`} 
              icon={Star} 
              color={stats.redemption_rate > 20 ? 'green' : 'red'} 
            />
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 mb-4">
          <div className="flex border-b">
            {['all', 'pending', 'approved'].map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-6 py-3 text-sm font-medium capitalize ${
                  filter === tab
                    ? 'border-b-2 border-[#c5a059] text-[#c5a059]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab} {tab === 'pending' && stats?.pending_reviews > 0 && (
                  <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                    {stats.pending_reviews}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Reviews Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coupon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      No reviews found
                    </td>
                  </tr>
                ) : (
                  reviews.map((review) => (
                    <ReviewRow
                      key={review.id}
                      review={review}
                      onApprove={handleApprove}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
